// Website template registry. Published templates come from the API; accepted
// AI drafts stay in IndexedDB until the SQL promotion workflow is enabled.
import { shallowReactive, ref } from 'vue'
import { API_BASE_URL, getAuthToken } from '@/stores/appConfig'

export const templatesLoaded = ref(false)
export const templates = shallowReactive([])

const LOCAL_DB_NAME = 'quotastic-builder'
const LOCAL_DB_VERSION = 1
const LOCAL_TEMPLATE_STORE = 'localTemplates'

function upsertTemplate(template) {
  const index = templates.findIndex(existing => existing.id === template.id)

  if (index >= 0) {
    templates.splice(index, 1, template)
  } else {
    templates.push(template)
  }

  return template
}

function openLocalTemplateDb() {
  return new Promise((resolve, reject) => {
    if (!window.indexedDB) {
      reject(new Error('This browser does not support local template storage.'))
      return
    }

    const request = window.indexedDB.open(LOCAL_DB_NAME, LOCAL_DB_VERSION)
    request.onerror = () => reject(request.error)
    request.onsuccess = () => resolve(request.result)
    request.onupgradeneeded = () => {
      const db = request.result

      if (!db.objectStoreNames.contains(LOCAL_TEMPLATE_STORE)) {
        const store = db.createObjectStore(LOCAL_TEMPLATE_STORE, { keyPath: 'id' })
        store.createIndex('companyId', 'companyId', { unique: false })
      }
    }
  })
}

async function withLocalTemplateStore(mode, operation) {
  const db = await openLocalTemplateDb()

  try {
    return await new Promise((resolve, reject) => {
      const transaction = db.transaction(LOCAL_TEMPLATE_STORE, mode)
      const store = transaction.objectStore(LOCAL_TEMPLATE_STORE)
      const request = operation(store)

      request.onerror = () => reject(request.error)
      request.onsuccess = () => resolve(request.result)
      transaction.onerror = () => reject(transaction.error)
    })
  } finally {
    db.close()
  }
}

export async function loadTemplates() {
  if (templatesLoaded.value) return

  try {
    const response = await fetch(`${API_BASE_URL}/templates`)
    if (!response.ok) throw new Error(`HTTP ${response.status}`)

    const { templates: fetched = [] } = await response.json()
    templates.splice(0, templates.length, ...fetched)
  } catch (error) {
    console.warn('[templates] loadTemplates failed:', error.message)
  } finally {
    templatesLoaded.value = true
  }
}

export async function generateTemplate({
  prompt,
  mode = 'modify',
  scope = 'design',
  companyId,
  baseTemplate,
}) {
  if (!baseTemplate) throw new Error('Choose a base template first.')
  if (!companyId) throw new Error('Company information is still loading.')

  const token = await getAuthToken()
  if (!token) throw new Error('You need to be signed in to generate templates.')

  const response = await fetch(`${API_BASE_URL}/ai/templates/generate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      prompt,
      mode,
      scope,
      company_id: companyId,
      base_template: baseTemplate,
    }),
  })
  const data = await response.json().catch(() => ({}))

  if (!response.ok) {
    throw new Error(data.error || 'Failed to generate template.')
  }

  return {
    ...data.template,
    local: true,
    proposal: true,
    ownerCompanyId: companyId,
  }
}

export async function updateTemplate({
  templateId,
  prompt,
  scope = 'design',
  companyId,
}) {
  const template = templates.find(item => item.id === templateId)
  if (!template) throw new Error('Template not found.')

  return generateTemplate({
    prompt,
    mode: 'modify',
    scope,
    companyId,
    baseTemplate: template,
  })
}

export function stageLocalTemplate(template) {
  return upsertTemplate({
    ...template,
    local: true,
  })
}

export function removeTemplateFromRegistry(templateId) {
  const index = templates.findIndex(template => template.id === templateId)
  if (index >= 0) templates.splice(index, 1)
}

export async function saveLocalTemplate(template, companyId) {
  const saved = {
    ...template,
    local: true,
    proposal: false,
    ownerCompanyId: companyId,
    acceptedAt: new Date().toISOString(),
  }

  await withLocalTemplateStore('readwrite', store => store.put({
    id: saved.id,
    companyId,
    template: saved,
    updatedAt: Date.now(),
  }))

  return upsertTemplate(saved)
}

export async function loadLocalTemplates(companyId) {
  if (!companyId) return []

  const records = await withLocalTemplateStore(
    'readonly',
    store => store.index('companyId').getAll(companyId)
  )
  const localTemplates = records.map(record => record.template)
  localTemplates.forEach(upsertTemplate)
  return localTemplates
}

export async function deleteLocalTemplate(templateId) {
  await withLocalTemplateStore(
    'readwrite',
    store => store.delete(templateId)
  )
  removeTemplateFromRegistry(templateId)
}
